/* eslint class-methods-use-this: 0 */
import {
  AdBridgeAgentStates,
  AdBridgeAgentTypes,
} from 'client/util/Constants/DirectoryConstants';
import ActiveDirectoryApi from 'client/api/v2/ActiveDirectoryApi';
import CollectionApi from 'client/api/v2/CollectionApi';
import IdsourceApi from 'client/api/v2/IdsourceApi';

const { active, inactive, unsealed } = AdBridgeAgentStates;

/**
 * The AdBridgeAgentApiClass returns a normalized array of the import agents
 * and sync agents for the current AD Bridge domain. The immport agents are
 * returned from GET /api/idsources/filter=dn:eq:${domain} and the sync agents
 * are returned from GET /api/v2/activedirectories/${id}/agents. Each type of
 * agent has slightly different fields that are mapped to a common format so
 * that they can be displayed consistently in the UI.
 */
export class AdBridgeAgentApiClass extends CollectionApi {
  constructor(id = '') {
    super(`/api/v2/activedirectories/${id}/agents`, {
      skip: 0,
      limit: 100,
      sort: 'hostname',
    });
  }

  async fetchAll(domain, id, sortField) {
    const importAgents = await IdsourceApi.fetchAllAgents(domain);
    const syncAgents = await ActiveDirectoryApi.fetchAllAgents(id);

    const agents = AdBridgeAgentApiClass.normalizeAgents(importAgents, syncAgents);
    AdBridgeAgentApiClass.sortAgents(agents, sortField);

    return agents;
  }

  /**
   * The import and sync agents have different fields and come from different
   * data stores, but are displayed in the same table in the UI. So here the
   * two agent types are normalized into a common format for display in the UI.
   */
  static normalizeAgents(importAgents, syncAgents) {
    const createAgent = agent => ({
      clusterSize: agent.nClusterSize,
      hostname: agent.name || agent.hostname,
      id: agent._id || agent.id,
      ipAddress: agent.ipAddress || agent.source_ip,
      lastContact: agent.lastUpdateTime || agent.contactAt,
      version: agent.version,
    });

    const normalizedImportAgents = importAgents
      .map(importAgent => ({
        ...createAgent(importAgent),
        state: importAgent.active ? active : inactive,
        type: AdBridgeAgentTypes.importAgent,
      }));

    const clusterSize = syncAgents.filter(syncAgent => syncAgent.state !== unsealed).length;

    const normalizedSyncAgents = syncAgents
      .map(syncAgent => ({
        ...createAgent(syncAgent),
        clusterSize,
        state: syncAgent.state,
        type: AdBridgeAgentTypes.syncAgent,
      }));

    return normalizedImportAgents.concat(normalizedSyncAgents);
  }

  static sortAgents(agents, sortField) {
    if (!sortField) {
      return;
    }

    agents.sort((a, b) => {
      const descending = !!String(sortField).match(/^-/);
      const aHostname = String(a.hostname || '').toLowerCase();
      const bHostname = String(b.hostname || '').toLowerCase();

      if (descending) {
        if (aHostname > bHostname) {
          return -1;
        }
        if (aHostname < bHostname) {
          return 1;
        }
        return 0;
      }

      // Ascending order by hostname sort
      if (aHostname < bHostname) {
        return -1;
      }
      if (aHostname > bHostname) {
        return 1;
      }
      return 0;
    });
  }
}

export default new AdBridgeAgentApiClass();
